/*  THE PREVIEW PLAYER for a full kit page. Web Audio, one buffer, scrubbable.

    IT IS ECHO VALLEY'S PLAYER, and deliberately so: that one is the standard for audio
    on this site. Decode into memory, play from an AudioBufferSourceNode on the context
    clock, and let the pointer land anywhere to play from there. An <audio> element was
    tried here first and dropped for the reasons written into ev-audio.js: setting
    currentTime on a playing element forces a re-buffer, so seeking stutters and the
    progress readout lags behind what you are hearing.

    LONG PREVIEWS LOOP, SHORT ONES DO NOT, and the line is drawn at three seconds. Every
    preview used to loop, which is right for a phrase and awful for a drum: Focused clap
    is 70 MILLISECONDS long and Contrast snare is 80, both peaking at full scale, so
    looping them produced a machine gun. The kit's files split cleanly either side:

        clap 0.07s   snare 0.08s   perc 1.22s   808 1.69s        <- do not loop
        one shot 6.32s   phrase 6.94s   granular 8.39s
        drum loop 10.97s   vocal 11.08s   soundscape 16.00s      <- loop

    A gap of four and a half seconds means three is not a judgement call. Duration decides
    rather than a per track flag, because a flag is one more thing to get wrong.

    EVERY PREVIEW IS TRIMMED TO A MATCHED LEVEL. ev-gain.js carries a multiplier per file,
    measured offline; without it the loudest preview was 15.9 times the quietest and
    stepping from an Analog Lab preset to a drum was an eight fold jump in volume. The
    trim is applied to the gain node at the moment playback begins, so it costs nothing
    and cannot drift out of sync with what is playing. A file with no entry plays at 1.0.

    WHAT PLAYS ONCE HAS TO END ITSELF. A looping source only fires onended when something
    stops it; a one shot reaches the end on its own, and without a handler the row would
    sit lit and the readout would count over silence. halt() and finish() both tear down
    and differ only in the position: a stop keeps it, running out rewinds to zero so
    pressing play replays the sound rather than its last millisecond. halt() clears
    onended before stop(), or stopping would fire finish() and throw the position away.

    THE WAVEFORM IS OPTIONAL, and where there is one it is also the scrubber.

    TWO COLOURS, OPTIONALLY. --kit-a is the shape; --kit-a2, if a section declares one, is
    what has already played. Left undeclared it falls back to --kit-a.

    THE SHAPES ARE ALREADY KNOWN. ev-peaks.js ships every preview's waveform, matched
    WITHOUT the file extension because the previews were WAV when the shapes were computed
    and are AAC now. ev-gain.js and ev-dur.js use the same convention.

    IT REPORTS WHERE IT IS through window.evKitAudio, and a panel exposes its own select()
    as panel.evKitSelect. See ev-bank-now.js.

    ONE SOUND AT A TIME, and nothing is fetched until something is pressed.  */
(function () {
  'use strict';

  var AC = window.AudioContext || window.webkitAudioContext;
  var panels = [].slice.call(document.querySelectorAll('[data-ev-deck-panel]'));
  if (!panels.length || !AC) return;

  /*  measured, not guessed. See the note above.  */
  var LOOP_MIN = 3;

  var ctx = null;
  function audio () { if (!ctx) ctx = new AC(); return ctx; }

  var buffers = {}, loading = {}, peaks = {};

  function stem (s) {
    return s.split('/').pop().split('?')[0].replace(/\.[A-Za-z0-9]+$/, '');
  }

  /*  the level trim for one preview, or unity if it was never measured  */
  function trim (u) {
    var g = (window.EV_GAIN || {})[stem(u)];
    return (typeof g === 'number' && g > 0) ? g : 1;
  }

  (function () {
    var src = window.EV_PEAKS || {};
    Object.keys(src).forEach(function (name) {
      var bin = atob(src[name]), out = new Array(bin.length);
      for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) / 255;
      peaks[stem(name)] = out;
    });
  })();

  function peaksFor (url) { return peaks[stem(url)] || null; }

  function load (url) {
    if (buffers[url]) return Promise.resolve(buffers[url]);
    if (loading[url]) return loading[url];
    var c = audio();
    loading[url] = fetch(url)
      .then(function (r) { return r.arrayBuffer(); })
      .then(function (ab) {
        return new Promise(function (res, rej) { c.decodeAudioData(ab, res, rej); });
      })
      .then(function (buf) {
        buffers[url] = buf;
        delete loading[url];
        if (!peaksFor(url)) {
          var ch = buf.getChannelData(0), N = 150, block = Math.floor(ch.length / N) || 1, out = [];
          for (var i = 0; i < N; i++) {
            var p = 0;
            for (var k = 0; k < block; k += 32) { var s = Math.abs(ch[i * block + k] || 0); if (s > p) p = s; }
            out.push(p);
          }
          var top = Math.max.apply(null, out) || 1;
          peaks[stem(url)] = out.map(function (v) { return v / top; });
        }
        return buf;
      })
      .catch(function (e) { delete loading[url]; throw e; });
    return loading[url];
  }

  var active = null;
  window.evKitAudio = { state: function () { return active ? active() : null; } };

  var all = [];
  function stopOthers (me) { all.forEach(function (p) { if (p !== me) p.halt(); }); }

  panels.forEach(function (panel) {
    var cv   = panel.querySelector('[data-ev-viz]');
    var rows = [].slice.call(panel.querySelectorAll('[data-ev-track]'));
    if (!rows.length) return;

    var url = rows[0].getAttribute('data-ev-track');
    var row = rows[0];
    var src = null, gain = null;
    var playing = false, scrub = false, hover = -1, looping = false;
    var startedAt = 0, offset = 0, dur = 0, raf = 0;

    function host () { return (cv || panel).closest('.ev-kit') || document.documentElement; }
    function token (name, fallback) {
      var v = getComputedStyle(host()).getPropertyValue(name).trim();
      return v || fallback;
    }
    function rgba (hex, a) {
      var v = hex.replace('#', '');
      if (v.length === 3) v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
      return 'rgba(' + parseInt(v.substr(0, 2), 16) + ',' + parseInt(v.substr(2, 2), 16) + ',' +
             parseInt(v.substr(4, 2), 16) + ',' + a + ')';
    }

    function pos () {
      if (!playing || !dur) return offset && dur ? offset / dur : 0;
      var t = audio().currentTime - startedAt + offset;
      return looping ? (t % dur) / dur : Math.min(1, t / dur);
    }

    function paint () {
      if (!cv) return;
      var data = peaksFor(url);
      var c = cv.getContext('2d'), w = cv.width, h = cv.height;
      var shape = token('--kit-a', '#7A5CFF');
      var lit   = token('--kit-a2', shape);
      c.clearRect(0, 0, w, h);
      if (!data) return;
      var p = pos(), n = data.length, gap = w / n * 0.34, bw = (w - gap * (n - 1)) / n;
      for (var k = 0; k < n; k++) {
        var bh = Math.max(2, data[k] * h * 0.9);
        var done = p > 0 && (k / n) <= p;
        var near = hover >= 0 && Math.abs(k / n - hover) < 0.012;
        c.fillStyle = done ? lit : rgba(shape, near ? .5 : .2);
        c.fillRect(k * (bw + gap), (h - bh) / 2, bw, bh);
      }
      var at = hover >= 0 ? hover : (playing ? p : -1);
      if (at >= 0) { c.fillStyle = lit; c.fillRect(Math.round(at * w) - 1, 0, 2, h); }
    }

    function tick () { raf = requestAnimationFrame(tick); if (playing && !scrub) paint(); }

    function begin (at) {
      var c = audio();
      gain = c.createGain();
      gain.gain.value = trim(url);          /*  the matched level. See ev-gain.js  */
      gain.connect(c.destination);
      src = c.createBufferSource();
      src.buffer = buffers[url]; src.connect(gain);
      dur = src.buffer.duration;
      looping = dur >= LOOP_MIN;
      src.loop = looping;
      var t0 = c.currentTime + 0.02;
      src.start(t0, at % dur);
      startedAt = t0; offset = at % dur;
      playing = true;
      src.onended = finish;
      row.classList.add('is-on');
      active = function () {
        return { url: url, stem: stem(url), p: pos(), t: pos() * dur, duration: dur, row: row };
      };
      cancelAnimationFrame(raf); raf = requestAnimationFrame(tick);
    }

    function teardown () {
      if (src) { try { src.onended = null; src.stop(); } catch (e) {} src.disconnect(); src = null; }
      if (gain) { gain.disconnect(); gain = null; }
      playing = false;
      active = null;
      rows.forEach(function (r) { r.classList.remove('is-on'); });
      cancelAnimationFrame(raf); raf = 0;
    }

    /*  stopped by hand: hold the position so pressing play resumes  */
    function halt () {
      if (!playing) return;
      offset = (audio().currentTime - startedAt + offset) % dur;
      teardown(); paint();
    }

    /*  ran out on its own: rewind, so play replays the sound  */
    function finish () {
      if (!playing) return;
      offset = 0;
      teardown(); paint();
    }

    var api = { halt: halt };
    all.push(api);

    function select (r, andPlay, at) {
      var next = r.getAttribute('data-ev-track');
      if (next !== url) { halt(); url = next; offset = 0; }
      rows.forEach(function (x) { x.classList.toggle('is-sel', x === r); });
      row = r;
      paint();
      if (!andPlay) return;
      var c = audio();
      if (c.state === 'suspended') c.resume();
      panel.classList.add('is-loading');
      load(url).then(function () {
        panel.classList.remove('is-loading');
        stopOthers(api);
        if (playing) halt();
        begin(at || 0);
        paint();
      }).catch(function () { panel.classList.remove('is-loading'); });
    }

    panel.evKitSelect = select;

    rows.forEach(function (r) {
      r.addEventListener('click', function () {
        if (r === row && playing) { halt(); return; }
        select(r, true, r === row ? offset : 0);
      });
    });
    rows[0].classList.add('is-sel');

    if (cv) {
      var pct = function (e) {
        var r = cv.getBoundingClientRect();
        return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      };
      cv.addEventListener('pointermove', function (e) { hover = pct(e); paint(); });
      cv.addEventListener('pointerleave', function () { hover = -1; paint(); });
      cv.addEventListener('pointerdown', function (e) {
        scrub = true; hover = pct(e);
        try { cv.setPointerCapture(e.pointerId); } catch (err) {}
        paint(); e.preventDefault();
      });
      var release = function (e) {
        if (!scrub) return;
        scrub = false;
        var p = pct(e); hover = p;
        var c = audio();
        if (c.state === 'suspended') c.resume();
        panel.classList.add('is-loading');
        load(url).then(function () {
          panel.classList.remove('is-loading');
          stopOthers(api);
          var to = p * buffers[url].duration;
          if (playing) halt();
          begin(to);
          paint();
        }).catch(function () { panel.classList.remove('is-loading'); });
      };
      cv.addEventListener('pointerup', release);
      cv.addEventListener('pointercancel', function () { scrub = false; paint(); });
    }

    paint();
  });

  window.evKitAudioStop = function () { all.forEach(function (p) { p.halt(); }); };

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) all.forEach(function (p) { p.halt(); });
  });
})();